import type { Vec2, Vec3, Ring } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { orientedFace } from '../solids';
import { panelWall, panelPoint, type Panel } from '../panels';
import { edgePanel } from './masonry';

/** An open arch in wall coordinates: a along the wall, springing at `spring`; round unless pointed. */
export interface Arch {
  readonly center: number;
  readonly width: number;
  readonly spring: number;
  /** Equilateral Gothic arch: two arcs of radius `width` struck from the opposite springings. */
  readonly pointed?: boolean;
}

export const ARCADE = {
  pier: 1.4,
  bay: 5.5,
  minArches: 3,
  crownDrop: 0.6,
  segments: 6,
} as const;

export function arcadeArches(length: number, height: number): Arch[] {
  const count = Math.max(ARCADE.minArches, Math.round(length / ARCADE.bay));
  const width = (length - (count + 1) * ARCADE.pier) / count;
  const spring = height - ARCADE.crownDrop - width / 2;
  return Array.from({ length: count }, (_, k) => ({
    center: ARCADE.pier + width / 2 + k * (width + ARCADE.pier),
    width,
    spring,
  }));
}

/* The crown of an equilateral arch from its left springing over the apex to its right springing. */
function pointedCrown(arch: Arch): Vec2[] {
  const w = arch.width;
  const left = arch.center - w / 2;
  const right = arch.center + w / 2;
  const n = ARCADE.segments / 2;
  const rise = Array.from({ length: n }, (_, i): Vec2 => {
    const angle = Math.PI - ((i + 1) * (Math.PI / 3)) / n;
    return [right + w * Math.cos(angle), arch.spring + w * Math.sin(angle)];
  });
  const fall = rise.slice(0, -1).reverse().map((p): Vec2 => [left + right - p[0], p[1]]);
  return [...rise, ...fall];
}

/** The arch's edge from the left jamb foot over the crown to the right jamb foot. */
export function archPath(arch: Arch): Vec2[] {
  const r = arch.width / 2;
  const arc = arch.pointed
    ? pointedCrown(arch)
    : Array.from({ length: ARCADE.segments - 1 }, (_, i): Vec2 => {
        const angle = Math.PI * (1 - (i + 1) / ARCADE.segments);
        return [arch.center + r * Math.cos(angle), arch.spring + r * Math.sin(angle)];
      });
  return [
    [arch.center - r, 0],
    [arch.center - r, arch.spring],
    ...arc,
    [arch.center + r, arch.spring],
    [arch.center + r, 0],
  ];
}

/** Counter-clockwise wall outline with the arches cut up from its foot. */
export function arcadeOutline(length: number, height: number, arches: readonly Arch[]): Ring {
  return [
    [0, 0],
    ...[...arches].sort((a, b) => a.center - b.center).flatMap(archPath),
    [length, 0],
    [length, height],
    [0, height],
  ];
}

export interface ArcadeStyles {
  readonly front: VertexStyle;
  readonly back: VertexStyle;
  readonly soffit: VertexStyle;
}

function offset(p: Vec3, d: Vec3, t: number): Vec3 {
  return [p[0] + d[0] * t, p[1] + d[1] * t, p[2] + d[2] * t];
}

function soffits(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  panel: Panel,
  arches: readonly Arch[],
  depth: { readonly inward: Vec3; readonly thickness: number }
): void {
  for (const arch of arches) {
    const path = archPath(arch);
    for (let i = 0; i + 1 < path.length; i++) {
      const a = path[i]!;
      const b = path[i + 1]!;
      const fa = panelPoint(panel, a[0], a[1]);
      const fb = panelPoint(panel, b[0], b[1]);
      const into = panelPoint(panel, arch.center, arch.spring);
      const mid = panelPoint(panel, (a[0] + b[0]) / 2, (a[1] + b[1]) / 2);
      const hint: Vec3 = [into[0] - mid[0], into[1] - mid[1], into[2] - mid[2]];
      const quad = [fa, fb, offset(fb, depth.inward, depth.thickness), offset(fa, depth.inward, depth.thickness)];
      orientedFace(builder, style, frame, quad, hint);
    }
  }
}

/** Outward plan normal of a counter-clockwise edge, and the edge's length. */
function outwardOf(edge: readonly [Vec2, Vec2]): { readonly n: Vec2; readonly length: number } {
  const [p, q] = edge;
  const length = Math.hypot(q[0] - p[0], q[1] - p[1]);
  return { n: [(q[1] - p[1]) / length, -(q[0] - p[0]) / length], length };
}

/** The reveals of arches cut through a wall on a counter-clockwise plan edge, `thickness` deep. */
export function archSoffits(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  edge: readonly [Vec2, Vec2],
  thickness: number,
  arches: readonly Arch[]
): void {
  const { n } = outwardOf(edge);
  const inward: Vec3 = [-n[0], 0, n[1]];
  soffits(builder, style, frame, edgePanel(edge[0], edge[1]), arches, { inward, thickness });
}

/**
 * Arcade wall along a counter-clockwise plan edge: its outer face, its inner face toward
 * the loggia and the arch soffits between them. Ends and top belong to the adjoining walls.
 */
export function buildArcade(
  builder: MeshBuilder,
  styles: ArcadeStyles,
  frame: Frame,
  edge: readonly [Vec2, Vec2],
  size: { readonly thickness: number; readonly height: number },
  arches: readonly Arch[]
): void {
  const [p, q] = edge;
  const { n, length } = outwardOf(edge);
  const outline = arcadeOutline(length, size.height, arches);
  const back: [Vec2, Vec2] = [
    [q[0] - n[0] * size.thickness, q[1] - n[1] * size.thickness],
    [p[0] - n[0] * size.thickness, p[1] - n[1] * size.thickness],
  ];
  const mirrored = outline.map((pt): Vec2 => [length - pt[0], pt[1]]);
  panelWall(builder, { wall: styles.front, opening: styles.front }, frame, edgePanel(p, q), outline, []);
  panelWall(builder, { wall: styles.back, opening: styles.back }, frame, edgePanel(back[0], back[1]), mirrored, []);
  archSoffits(builder, styles.soffit, frame, edge, size.thickness, arches);
}
